import useEditingStore from '@hooks/useEditingStore';
import useTemplateFeature from '@hooks/useTemplateFeature';
import { $curScene, $tourData } from '@stores/dataStores';
import {
  $GateSearchBtn,
  $editingFlag,
  $editingLangFlag,
  $editingUIColor,
} from '@stores/editingStores';
import { $isCn } from '@stores/langStores';
import { type EditConfigUI, SceneType, TemplateId } from '@types';
import { getTemplateConfig } from '@utils/templateConfig';
import { useStore } from 'effector-react';
import type { ReactNode } from 'react';

export function doubleAlpha(rgba?: string): string {
  if (!rgba) return '';
  const [r, g, b, a] = rgba.split(',').map((v) => Number.parseFloat(v));
  const newA = Math.min(a * 2, 1);
  return `${r},${g},${b},${newA}`;
}

export function WFeatureTogglesVip() {
  const isMatrix = useStore($curScene)?.type === SceneType.matrix;
  const isCn = useStore($isCn);
  const langFeature = useTemplateFeature('language');
  const searchFeature = useTemplateFeature('searchSetting');
  const flag = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.flag,
    $editing: $editingFlag.store,
  });
  const langFlag = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.config?.language?.showButton,
    $editing: $editingLangFlag.store,
  });
  const showSearch = useEditingStore({
    $data: $tourData,
    dataFn: (state) => !!state.config?.search?.enabled,
    $editing: $GateSearchBtn.state,
  });
  const ui = useEditingStore({
    $data: $tourData,
    dataFn: (state) => {
      const ui = state.config?.ui;
      const defaultUI = getTemplateConfig(TemplateId.vip)('uiColor') ?? {};
      if (!ui) return defaultUI;
      return { ...defaultUI, ...ui };
    },
    $editing: $editingUIColor.store,
  });

  const showFS = !!(flag?.showFullscreenBtn ?? 1);
  const showVR = !!(flag?.showVRBtn ?? 1) && !isMatrix;
  const showVS = !!(flag?.showViewSwitchBtn ?? 1) && !isMatrix;
  const showLang = langFeature && !!(langFlag ?? 0);
  const showHotspotBtn = !!(flag?.showHotspotBtn ?? 0);

  return (
    <div className="absolute right-15 top-15 flex flex-col">
      {showFS && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="m20.04 3.753-4.435.55a.187.187 0 0 0-.11.317l1.282 1.282-3.598 3.597a.188.188 0 0 0 0 .265l1.057 1.057a.188.188 0 0 0 .265 0l3.6-3.6 1.282 1.282c.11.11.297.045.316-.11l.549-4.432a.185.185 0 0 0-.209-.208ZM9.763 13.179a.188.188 0 0 0-.265 0l-3.597 3.6-1.283-1.282a.186.186 0 0 0-.316.11l-.55 4.432a.187.187 0 0 0 .208.209l4.434-.551a.187.187 0 0 0 .11-.316l-1.282-1.282 3.6-3.6a.188.188 0 0 0 0-.265l-1.059-1.055Z"
            />
          )}
        </ToggleIcon>
      )}
      {showHotspotBtn && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              d="M12 20a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm-7.072-2.344a1.002 1.002 0 0 1 1.415 0 1 1 0 1 1-1.415 0Zm14.143.001a.999.999 0 1 1-1.414 1.414.999.999 0 1 1 1.414-1.414ZM12 8a4 4 0 1 1 0 8 4 4 0 0 1 0-8Zm9 3a1 1 0 1 1 0 2 1 1 0 0 1 0-2ZM3 11a1 1 0 1 1 0 2 1 1 0 0 1 0-2Zm16.071-6.071a1 1 0 1 1-1.413 1.415 1 1 0 0 1 1.413-1.415Zm-14.142 0a.999.999 0 1 1 1.414 1.414.999.999 0 1 1-1.414-1.414ZM12 2a1 1 0 1 1 0 2 1 1 0 0 1 0-2Z"
            />
          )}
        </ToggleIcon>
      )}
      {showVR && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M22 5.5H2v13h7v-1.086l2.854-2.853 3.12 3.121.026-.025v.843h7v-13Zm-9.854 6.525-.292.293-.293-.293-4.243 4.243.232.232H4v-9h16v9h-3.843l.232-.232-4.242-4.243ZM7.5 12.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm9 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z"
            />
          )}
        </ToggleIcon>
      )}
      {showVS && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M20.199 3.836c-.662-.66-1.984-1.276-4.54-.352a.893.893 0 0 0-.485 1.144c.176.44.66.66 1.102.528 1.278-.44 2.248-.484 2.689-.088.484.484.352 1.76-.353 3.344a22.094 22.094 0 0 1-4.32 5.94 21.466 21.466 0 0 1-5.95 4.311c-1.587.704-2.866.836-3.35.352-.485-.484-.353-1.76.352-3.344q0-.043.044-.087c.027-.028.021-.038.012-.052-.005-.01-.012-.02-.012-.037v-.44c0-.043 0-.043-.044-.088-1.587-3.651.088-7.92 3.747-9.503.925-.396 1.895-.616 2.865-.616.397 0 .793.044 1.19.088.485.088.97-.264 1.058-.748.088-.484-.264-.968-.75-1.056A8.13 8.13 0 0 0 12 3c-4.98 0-8.992 4.004-8.992 8.932 0 1.144.22 2.244.617 3.3-.882 2.2-.837 3.96.176 4.971.573.572 1.41.836 2.204.792a7.552 7.552 0 0 0 2.777-.66c4.629 1.804 9.83-.528 11.593-5.148a9.085 9.085 0 0 0 0-6.467c.882-2.156.837-3.872-.176-4.884ZM8.253 8.984a.401.401 0 0 1-.352.176.477.477 0 0 1-.309-.132c-.176-.132-.22-.44-.044-.616.265-.264.485-.528.75-.748a6.241 6.241 0 0 1 3.438-1.496c.22 0 .44.176.484.396 0 .22-.176.44-.396.484-1.102.176-2.116.616-2.954 1.32a4.08 4.08 0 0 0-.617.616Zm-1.542.748c.088-.22.352-.308.573-.22.22.132.308.352.264.528l-.132.264c-.088.22-.132.44-.22.66-.045.176-.221.308-.441.308h-.133a.39.39 0 0 1-.308-.528c.044-.264.176-.484.264-.748l.133-.264Zm5.937 9.327c-.272 0-.543-.039-.815-.078 1.436-.89 2.755-1.974 3.957-3.175 1.203-1.2 2.289-2.516 3.181-3.95.465 3.447-1.94 6.66-5.43 7.125-.272.078-.583.078-.893.078Z"
            />
          )}
        </ToggleIcon>
      )}
      {showLang && isCn && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M10.904 17v-1.773h-5.62v-2.363h5.117V11H5.284V8.864h5.483V7H3v10h7.904Zm3.289 0v-5.5c0-.697-.015-1.258-.046-1.682l.548.91L18.67 17H21V7h-2.147v5.59c0 .668.015 1.168.045 1.5v.046c-.06-.181-.197-.439-.41-.772a2.233 2.233 0 0 1-.138-.228L14.513 7h-2.467v10h2.147Z"
            />
          )}
        </ToggleIcon>
      )}
      {showLang && !isCn && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <path
              fill={color}
              fillRule="evenodd"
              d="M7.06 17c2.49 0 4.002-1.101 4.538-3.304l-2.27-.66c-.22 1.409-.977 2.128-2.269 2.158-1.639-.118-2.49-1.19-2.553-3.216.094-1.997.946-3.04 2.553-3.128 1.135.059 1.86.588 2.175 1.586l2.27-.484C10.81 8.042 9.329 7.059 7.059 7 3.844 7.176 2.158 8.836 2 11.978c.158 3.201 1.844 4.875 5.06 5.022Zm7.895-.176v-5.33c0-.676-.016-1.22-.047-1.63l.567.88 4.114 6.08H22V7.132h-2.222v5.419c0 .646.016 1.13.047 1.453v.044c-.063-.176-.205-.425-.425-.748a2.135 2.135 0 0 1-.142-.22l-3.972-5.948h-2.553v9.692h2.222Z"
            />
          )}
        </ToggleIcon>
      )}
      {showSearch && searchFeature && (
        <ToggleIcon ui={ui}>
          {({ color }) => (
            <>
              <defs>
                <clipPath id="prefixXWqJOwQVcDgXQY9FFB1dca">
                  <rect width="24" height="24" rx="0" />
                </clipPath>
              </defs>
              <g clip-path="url(#prefixXWqJOwQVcDgXQY9FFB1dca)">
                <path
                  fill={color}
                  fillRule="evenodd"
                  d="M10.375 17.853a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM13.557 7.67q1.318 1.318 1.318 3.182 0 1.864-1.318 3.182-1.318 1.318-3.182 1.318-1.864 0-3.182-1.318-1.318-1.318-1.318-3.182 0-1.864 1.318-3.182 1.318-1.318 3.182-1.318 1.864 0 3.182 1.318Zm4.934 12.11-.013-.013-1.5-1.544a1.25 1.25 0 0 1 1.78-1.755l.014.013 1.5 1.544a1.25 1.25 0 0 1-1.78 1.755Z"
                />
              </g>
            </>
          )}
        </ToggleIcon>
      )}
    </div>
  );
}

function ToggleIcon({
  ui,
  children,
}: { ui: EditConfigUI; children: (props: { color: string }) => ReactNode }) {
  return (
    <div
      className="mb-10 h-32 w-32 rounded-full flex items-center justify-center"
      style={{
        backgroundColor: `rgba(${ui.bgColor})`,
      }}
    >
      <svg width={24} height={24} xmlnsXlink="http://www.w3.org/1999/xlink">
        {children({ color: `rgba(${ui.fontColor})` })}
      </svg>
    </div>
  );
}
